import {
  Timestamp,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  serverTimestamp,
  runTransaction,
  setDoc,
  type DocumentData,
  type Unsubscribe,
} from 'firebase/firestore'
import { getFirebaseServices } from '../lib/firebase'
import { taskAuditOperationDecision } from './audit'
import {
  TASK_AUDIT_ACTION_TYPES,
  type PlannerTask,
  type TaskAuditEvent,
  type TaskAuditSnapshot,
} from './types'

function tasksPath(userId: string) {
  const { firestore } = getFirebaseServices()
  return collection(firestore, 'users', userId, 'tasks')
}

function taskEventsPath(userId: string) {
  const { firestore } = getFirebaseServices()
  return collection(firestore, 'users', userId, 'taskEvents')
}

function isPlannerTask(value: DocumentData): value is PlannerTask {
  return (
    typeof value.id === 'string' &&
    typeof value.title === 'string' &&
    typeof value.notes === 'string' &&
    typeof value.dueAt === 'string' &&
    ['low', 'normal', 'high', 'critical'].includes(value.priority) &&
    typeof value.completed === 'boolean' &&
    (value.reminderMinutes === null || typeof value.reminderMinutes === 'number') &&
    typeof value.createdAt === 'string' &&
    typeof value.updatedAt === 'string'
  )
}

function isAuditSnapshot(value: unknown): value is TaskAuditSnapshot {
  if (!value || typeof value !== 'object') return false
  const snapshot = value as Record<string, unknown>
  return (
    typeof snapshot.title === 'string' &&
    typeof snapshot.notes === 'string' &&
    typeof snapshot.dueAt === 'string' &&
    ['low', 'normal', 'high', 'critical'].includes(String(snapshot.priority)) &&
    typeof snapshot.completed === 'boolean' &&
    (snapshot.reminderMinutes === null || typeof snapshot.reminderMinutes === 'number')
  )
}

function normalizeTaskAuditEvent(value: DocumentData): TaskAuditEvent | null {
  const occurredAt =
    value.occurredAt instanceof Timestamp
      ? value.occurredAt.toDate().toISOString()
      : typeof value.occurredAt === 'string'
        ? value.occurredAt
        : typeof value.clientOccurredAt === 'string'
          ? value.clientOccurredAt
          : null
  const actions = Array.isArray(value.actions) ? value.actions : []

  if (
    !occurredAt ||
    typeof value.id !== 'string' ||
    typeof value.taskId !== 'string' ||
    typeof value.clientOccurredAt !== 'string' ||
    typeof value.dueAt !== 'string' ||
    typeof value.taskTitle !== 'string' ||
    !(value.before === null || isAuditSnapshot(value.before)) ||
    !(value.after === null || isAuditSnapshot(value.after)) ||
    value.source !== 'user' ||
    !actions.length ||
    !actions.every(
      (item) =>
        item &&
        typeof item === 'object' &&
        TASK_AUDIT_ACTION_TYPES.includes(item.type) &&
        (item.before === null || typeof item.before === 'string') &&
        (item.after === null || typeof item.after === 'string'),
    )
  ) {
    return null
  }

  return {
    id: value.id,
    taskId: value.taskId,
    occurredAt,
    clientOccurredAt: value.clientOccurredAt,
    dueAt: value.dueAt,
    taskTitle: value.taskTitle,
    before: value.before,
    after: value.after,
    actions,
    source: 'user',
  }
}

export function watchPlannerTasks(
  userId: string,
  onTasks: (tasks: PlannerTask[], fromCache: boolean) => void,
  onError: (error: Error) => void,
): Unsubscribe {
  return onSnapshot(
    tasksPath(userId),
    { includeMetadataChanges: true },
    (snapshot) => {
      const tasks = snapshot.docs.map((item) => item.data()).filter(isPlannerTask)
      onTasks(tasks, snapshot.metadata.fromCache)
    },
    onError,
  )
}

export async function savePlannerTask(userId: string, task: PlannerTask): Promise<void> {
  const { firestore } = getFirebaseServices()
  await setDoc(doc(firestore, 'users', userId, 'tasks', task.id), task)
}

export async function deletePlannerTask(userId: string, taskId: string): Promise<void> {
  const { firestore } = getFirebaseServices()
  await deleteDoc(doc(firestore, 'users', userId, 'tasks', taskId))
}

export function watchTaskAuditEvents(
  userId: string,
  onEvents: (events: TaskAuditEvent[], fromCache: boolean) => void,
  onError: (error: Error) => void,
): Unsubscribe {
  return onSnapshot(
    taskEventsPath(userId),
    { includeMetadataChanges: true },
    (snapshot) => {
      const events = snapshot.docs
        .map((item) => normalizeTaskAuditEvent(item.data()))
        .filter((item): item is TaskAuditEvent => item !== null)
        .sort(
          (left, right) =>
            new Date(right.occurredAt).getTime() - new Date(left.occurredAt).getTime(),
        )
      onEvents(events, snapshot.metadata.fromCache)
    },
    onError,
  )
}

function eventWrite(event: TaskAuditEvent) {
  return {
    ...event,
    occurredAt: serverTimestamp(),
  }
}

export async function savePlannerTaskWithAudit(
  userId: string,
  task: PlannerTask,
  auditEvent: TaskAuditEvent | null,
): Promise<void> {
  const { firestore } = getFirebaseServices()
  const taskReference = doc(firestore, 'users', userId, 'tasks', task.id)

  await runTransaction(firestore, async (transaction) => {
    if (!auditEvent) {
      transaction.set(taskReference, task)
      return
    }

    const eventReference = doc(
      firestore,
      'users',
      userId,
      'taskEvents',
      auditEvent.id,
    )
    const existingEvent = await transaction.get(eventReference)
    const normalizedEvent = existingEvent.exists()
      ? normalizeTaskAuditEvent(existingEvent.data())
      : null
    if (existingEvent.exists() && !normalizedEvent) {
      throw new Error('task-audit-operation-conflict')
    }
    if (taskAuditOperationDecision(normalizedEvent, auditEvent) === 'noop') {
      return
    }

    transaction.set(taskReference, task)
    transaction.set(eventReference, eventWrite(auditEvent))
  })
}

export async function deletePlannerTaskWithAudit(
  userId: string,
  taskId: string,
  event: TaskAuditEvent,
): Promise<void> {
  const { firestore } = getFirebaseServices()
  const taskReference = doc(firestore, 'users', userId, 'tasks', taskId)
  const eventReference = doc(firestore, 'users', userId, 'taskEvents', event.id)

  await runTransaction(firestore, async (transaction) => {
    const existingEvent = await transaction.get(eventReference)
    const normalizedEvent = existingEvent.exists()
      ? normalizeTaskAuditEvent(existingEvent.data())
      : null
    if (existingEvent.exists() && !normalizedEvent) {
      throw new Error('task-audit-operation-conflict')
    }
    if (taskAuditOperationDecision(normalizedEvent, event) === 'noop') {
      return
    }

    transaction.delete(taskReference)
    transaction.set(eventReference, eventWrite(event))
  })
}
